import { format } from "date-fns";
import _ from "lodash";

import { Address } from "@models/Address";
import { State } from "@models/State";
import { SubState, isReyesSubState } from "@models/SubState";

/** Flat react-hook-form shape for address fields (not the API `Address` model). */
export type AddressFormValues = {
  id?: string;
  name: string;
  abn: string;
  defaultAttn: string;
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  zip: string;
  country: string;
  phoneNumber: string;
  isActive: boolean;
  stateId: string;
  subStateId: string;
  /** Mirrors `State.hasSubStateOptions` for the selected state. */
  stateHasSubStateOptions: boolean;
  changeNote: string;
  changeEffectiveDate: string;
  deactivationNote: string;
  /** Whether the address is for Reyes. */
  isForReyes: boolean;
  externalReyesId: string;
  isReyesSubState: boolean;
};

const EMPTY_ADDRESS_FORM_VALUES: AddressFormValues = {
  name: "",
  abn: "",
  defaultAttn: "",
  streetAddress1: "",
  streetAddress2: "",
  city: "",
  zip: "",
  country: "USA",
  phoneNumber: "",
  isActive: true,
  stateId: "",
  subStateId: "",
  stateHasSubStateOptions: false,
  changeNote: "",
  changeEffectiveDate: format(new Date(), "yyyy-MM-dd"),
  deactivationNote: "",
  isForReyes: false,
  externalReyesId: "",
  isReyesSubState: false,
};

/** Hong Kong addresses have no postal code in RTA. */
const COUNTRIES_WITHOUT_ZIP = new Set(["HK"]);

export const usesZipCode = (country: string) =>
  !COUNTRIES_WITHOUT_ZIP.has(country);

export const getAddressFormDefaultValues = (
  address?: Address | null
): AddressFormValues => {
  if (!address) {
    return EMPTY_ADDRESS_FORM_VALUES;
  }

  const country = address.state?.country ?? "USA";

  return {
    ...EMPTY_ADDRESS_FORM_VALUES,
    id: address.id,
    name: address.name,
    abn: address.abn,
    defaultAttn: address.defaultAttn ?? "",
    streetAddress1: address.streetAddress1,
    streetAddress2: address.streetAddress2 ?? "",
    city: address.city,
    zip: usesZipCode(country) ? (address.zip ?? "") : "",
    // address.country is unreliable, we need to ensure it matches an option in the dropdown
    // so we use the state.country
    country,
    phoneNumber: address.phoneNumber ?? "",
    isActive: address.isActive,
    stateId: String(address.state?.id ?? address.stateId ?? ""),
    subStateId: String(address.subState?.id ?? address.subStateId ?? ""),
    stateHasSubStateOptions: address.state?.hasSubStateOptions ?? false,
    deactivationNote: address.deactivationNote ?? "",
    isForReyes: address.isForReyes ?? false,
    externalReyesId: address.externalReyesId ?? "",
    isReyesSubState: isReyesSubState(address.subState),
  };
};

export const pickDirty = (
  values: AddressFormValues,
  dirtyKeys: string[]
): Partial<AddressFormValues> =>
  _.pick(values, dirtyKeys) as Partial<AddressFormValues>;

/** Scalar address attributes edited in forms and safe to send on create/update. */
const WRITABLE_ADDRESS_SCALAR_KEYS = [
  "name",
  "abn",
  "defaultAttn",
  "streetAddress1",
  "streetAddress2",
  "country",
  "city",
  "zip",
  "phoneNumber",
  "isActive",
  "isForReyes",
  "externalReyesId",
] as const satisfies readonly (keyof AddressFormValues)[];

type WritableAddressScalarKey = (typeof WRITABLE_ADDRESS_SCALAR_KEYS)[number];

/** Attributes accepted by POST/PATCH on `addresses`. */
export type AddressMutationAttributes = Partial<
  Pick<
    Address,
    WritableAddressScalarKey | "shippingAlertRecipients" | "deactivationNote"
  >
> & {
  state?: Pick<State, "id">;
  subState?: Pick<SubState, "id">;
};

export type CreateAddressPayload = AddressMutationAttributes & {
  type: Address["type"];
  territoryId?: string | number | null;
  marketId?: string;
  /** required when type == "change-request" */
  addressToChangeId?: string;
  /** required when type == "change-request" */
  changeNote?: string;
  /** required when type == "change-request" */
  changeEffectiveDate?: string;
};

export type UpdateAddressPayload = AddressMutationAttributes & {
  id: string;
  type?: Address["type"];
};

export const formValuesToMutationAttributes = (
  values: Partial<AddressFormValues>
): AddressMutationAttributes => {
  const scalars = _.pick(values, WRITABLE_ADDRESS_SCALAR_KEYS) as Partial<
    Pick<Address, WritableAddressScalarKey>
  >;

  if (scalars.isForReyes === false) {
    scalars.externalReyesId = null;
  } else if ("externalReyesId" in scalars) {
    // form uses "", API expects null
    scalars.externalReyesId ||= null;
  }

  if (values.country && !usesZipCode(values.country)) {
    scalars.zip = "";
  }

  return {
    ...scalars,
    ...(values.stateId && { state: { id: values.stateId } }),
    ...(values.subStateId && { subState: { id: values.subStateId } }),
  };
};

export const pickDirtyMutationFields = (
  values: AddressFormValues,
  dirtyKeys: string[]
): AddressMutationAttributes => {
  const attrs = formValuesToMutationAttributes(pickDirty(values, dirtyKeys));

  if (!usesZipCode(values.country)) {
    attrs.zip = "";
  }

  return attrs;
};
